import React, { useState, useMemo } from 'react';
import { Milestone, Action, MilestoneNode, ActionNode, MILESTONE_CATEGORY_LABELS } from '../types';
import { CAPABILITY_DOMAIN_LABELS, CapabilityDomain } from '../capabilities/types';
import InlineEditableText from './InlineEditableText';
import InlineEditableDate from './InlineEditableDate';
import CompletionDot from './CompletionDot';
import CollapseChevron from './CollapseChevron';
import ActionRow from './ActionRow';

// Support both Firebase types and legacy Node types for backward compatibility
type MilestoneType = Milestone | MilestoneNode;
type ActionType = Action | ActionNode;

// Origin labels for milestone metadata display
const ORIGIN_LABELS: Record<string, string> = {
  manual: 'Handmatig',
  sparring: 'Via sparring',
  capability: 'Via capaciteiten',
  selftest: 'Via zelftests',
};

interface MilestoneRowProps {
  milestone: MilestoneType;
  actions: ActionType[];
  onUpdateMilestone: (id: string, updates: Partial<MilestoneType>) => void;
  onToggleAction: (id: string) => void;
  onUpdateAction: (id: string, title: string) => void;
  onAddAction?: (milestoneId: string) => void;
  onDeleteAction?: (id: string) => void;
  onPlanAction?: (action: ActionType) => void;
  onDeleteMilestone?: (id: string) => void;
  defaultExpanded?: boolean;
  focusActionId?: string | null;  // NIEUW - ID van focus actie voor subtiele highlight
  startActionNumber?: number;  // NIEUW - startnummer voor acties nummering
}

/**
 * MilestoneRow
 * 
 * A collapsible row for milestones with nested actions.
 * - Title + target date (inline editable)
 * - Completion calculated from actions
 * - Actions are collapsible underneath
 */
const MilestoneRow: React.FC<MilestoneRowProps> = ({
  milestone,
  actions,
  onUpdateMilestone,
  onToggleAction,
  onUpdateAction,
  onAddAction,
  onDeleteAction,
  onPlanAction,
  onDeleteMilestone,
  defaultExpanded = false,
  focusActionId,  // NIEUW - doorgeven aan ActionRow
  startActionNumber,  // NIEUW - startnummer voor nummering
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [isHovered, setIsHovered] = useState(false);
  const [isAddingAction, setIsAddingAction] = useState(false);
  const [newActionTitle, setNewActionTitle] = useState('');

  // Calculate completion based on actions
  const isCompleted = useMemo(() => {
    if (actions.length === 0) return false;
    return actions.every(a => !!a.completed);
  }, [actions]);

  const completedCount = useMemo(() => {
    return actions.filter(a => !!a.completed).length;
  }, [actions]);

  // Group actions by domain
  const actionsByDomain = useMemo(() => {
    const grouped = new Map<string, { domain: CapabilityDomain; domainLabel: string; actions: ActionType[] }>();
    
    // Helper to detect domain from action
    const detectDomain = (action: ActionType): CapabilityDomain | null => {
      if ('domain' in action && action.domain) {
        return action.domain as CapabilityDomain;
      }
      // Try to detect from title
      const title = action.title.toLowerCase();
      const domainKeywords: Record<CapabilityDomain, string[]> = {
        strategy: ['strategie', 'richting', 'visie', 'plan', 'doel'],
        marketing: ['marketing', 'promotie', 'campagne', 'content', 'social media'],
        sales: ['verkoop', 'sales', 'klant', 'lead', 'conversie'],
        product: ['product', 'dienst', 'ontwikkeling', 'feature'],
        operations: ['operatie', 'proces', 'systeem', 'workflow'],
        finance: ['financiën', 'budget', 'geld', 'kosten'],
        team: ['team', 'medewerker', 'aannemen', 'werven'],
      };
      
      for (const [domain, keywords] of Object.entries(domainKeywords)) {
        if (keywords.some(kw => title.includes(kw))) {
          return domain as CapabilityDomain;
        }
      }
      
      return null;
    };
    
    for (const action of actions) {
      const domain = detectDomain(action);
      const domainKey = domain || 'other';
      const domainLabel = domain ? CAPABILITY_DOMAIN_LABELS[domain] : 'Overig';
      
      if (!grouped.has(domainKey)) {
        grouped.set(domainKey, {
          domain: domain || 'strategy', // Fallback
          domainLabel,
          actions: [],
        });
      }
      
      grouped.get(domainKey)!.actions.push(action);
    }
    
    return Array.from(grouped.values());
  }, [actions]);

  const handleAddAction = () => {
    if (newActionTitle.trim() && onAddAction) {
      onAddAction(milestone.id);
      // The parent will handle the actual creation
      setNewActionTitle('');
      setIsAddingAction(false);
    }
  };

  // Type guard to check if milestone is a Firebase Milestone (has category/origin)
  const isFirebaseMilestone = (m: MilestoneType): m is Milestone => {
    return 'ownerId' in m && 'createdAt' in m;
  };

  return (
    <div className="relative">
      {/* Connection line to parent */}
      <div className="absolute left-0 top-0 bottom-0 w-px bg-gray-200" style={{ left: '6px' }} />

      <div className="pl-6">
        {/* Milestone header */}
        <div
          className={`
            flex items-center justify-between
            py-2 px-2 -mx-2
            rounded-md
            transition-colors duration-150
            ${isHovered ? 'bg-gray-50' : ''}
          `.trim()}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Left side */}
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            {/* Expand chevron */}
            {actions.length > 0 && (
              <CollapseChevron
                expanded={expanded}
                onToggle={() => setExpanded(!expanded)}
                size="sm"
              />
            )}
            {actions.length === 0 && <div className="w-4" />}

            {/* Completion dot */}
            <CompletionDot completed={isCompleted} size="md" />

            {/* Title */}
            <InlineEditableText
              value={milestone.title}
              onSave={(title) => onUpdateMilestone(milestone.id, { title })}
              placeholder="Mijlpaal naam..."
              className={`
                text-sm font-medium flex-1 truncate
                ${isCompleted ? 'text-gray-400' : 'text-gray-800'}
              `}
            />
          </div>

          {/* Right side */}
          <div className="flex items-center space-x-3">
            {/* Target date */}
            <InlineEditableDate
              value={milestone.targetDate || ''}
              onSave={(date) => onUpdateMilestone(milestone.id, { targetDate: date })}
              placeholder="Geen datum"
              className="text-xs"
            />

            {/* Action count badge */}
            {actions.length > 0 && (
              <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                {completedCount}/{actions.length}
              </span>
            )}

            {/* Delete button (hover-only) */}
            {onDeleteMilestone && (
              <button
                onClick={() => onDeleteMilestone(milestone.id)}
                className={`
                  text-gray-400 hover:text-red-500
                  p-1 rounded
                  transition-all duration-150
                  ${isHovered ? 'opacity-100' : 'opacity-0'}
                `}
                title="Verwijder mijlpaal"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Metadata labels (category + origin) */}
        {isFirebaseMilestone(milestone) && (milestone.category || milestone.origin) && (
          <div className="pl-8 mt-1 flex items-center space-x-3 text-xs text-gray-500">
            {milestone.category && (
              <span>
                {MILESTONE_CATEGORY_LABELS[milestone.category]}
              </span>
            )}
            {milestone.origin && (
              <span>
                Aangemaakt via: {ORIGIN_LABELS[milestone.origin] || milestone.origin}
              </span>
            )}
          </div>
        )}

        {/* Actions list (collapsible) - grouped by domain */}
        {expanded && (
          <>
            {actions.length === 0 ? (
              <div className="pl-6 mt-1 space-y-1 border-l border-gray-100 ml-2">
                <p className="text-xs text-gray-400 py-2 italic">Nog geen acties</p>
                {/* Add action input */}
                {isAddingAction ? (
                  <div className="flex items-center space-x-2 py-1">
                    <input
                      type="text"
                      value={newActionTitle}
                      onChange={(e) => setNewActionTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAddAction();
                        if (e.key === 'Escape') {
                          setIsAddingAction(false);
                          setNewActionTitle('');
                        }
                      }}
                      onBlur={() => {
                        if (!newActionTitle.trim()) {
                          setIsAddingAction(false);
                        }
                      }}
                      placeholder="Nieuwe actie..."
                      className="
                        flex-1 px-2 py-1 text-xs
                        border border-gray-300 rounded-md
                        focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500
                      "
                      autoFocus
                    />
                    <button
                      onClick={handleAddAction}
                      className="text-xs text-orange-600 hover:text-orange-700 px-2"
                    >
                      Toevoegen
                    </button>
                  </div>
                ) : (
                  onAddAction && (
                    <button
                      onClick={() => setIsAddingAction(true)}
                      className="
                        text-xs text-gray-400 hover:text-gray-600
                        py-1 flex items-center space-x-1
                        transition-colors duration-150
                      "
                    >
                      <span>+</span>
                      <span>Nieuwe actie</span>
                    </button>
                  )
                )}
              </div>
            ) : actionsByDomain.length <= 1 ? (
              <div className="pl-6 mt-1 space-y-1 border-l border-gray-100 ml-2">
                {actions.map((action, index) => (
                  <ActionRow
                    key={action.id}
                    action={action}
                    onToggleComplete={onToggleAction}
                    onUpdateTitle={onUpdateAction}
                    onPlanInAgenda={onPlanAction}
                    onDelete={onDeleteAction}
                    isFocusAction={action.id === focusActionId}
                    actionNumber={startActionNumber !== undefined ? startActionNumber + index : undefined}
                    showPriorityBadge={true}
                    showTypeBadge={true}
                  />
                ))}
                {/* Add action input */}
                {isAddingAction ? (
                  <div className="flex items-center space-x-2 py-1">
                    <input
                      type="text"
                      value={newActionTitle}
                      onChange={(e) => setNewActionTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAddAction();
                        if (e.key === 'Escape') {
                          setIsAddingAction(false);
                          setNewActionTitle('');
                        }
                      }}
                      onBlur={() => {
                        if (!newActionTitle.trim()) {
                          setIsAddingAction(false);
                        }
                      }}
                      placeholder="Nieuwe actie..."
                      className="
                        flex-1 px-2 py-1 text-xs
                        border border-gray-300 rounded-md
                        focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500
                      "
                      autoFocus
                    />
                    <button
                      onClick={handleAddAction}
                      className="text-xs text-orange-600 hover:text-orange-700 px-2"
                    >
                      Toevoegen
                    </button>
                  </div>
                ) : (
                  onAddAction && (
                    <button
                      onClick={() => setIsAddingAction(true)}
                      className="
                        text-xs text-gray-400 hover:text-gray-600
                        py-1 flex items-center space-x-1
                        transition-colors duration-150
                      "
                    >
                      <span>+</span>
                      <span>Nieuwe actie</span>
                    </button>
                  )
                )}
              </div>
            ) : (
              <div className="pl-6 mt-1 space-y-3 border-l border-gray-100 ml-2">
                {(() => {
                  let actionCounter = startActionNumber || 0;
                  return actionsByDomain.map(({ domain, domainLabel, actions: domainActions }) => (
                    <div key={domain} className="space-y-1">
                      {/* Domain header */}
                      <div className="flex items-center gap-2 mb-1 pt-1">
                        <span className="text-xs font-medium text-gray-600">
                          {domainLabel}
                        </span>
                        <span className="text-xs text-gray-400">
                          ({domainActions.length} {domainActions.length === 1 ? 'actie' : 'acties'})
                        </span>
                      </div>
                      
                      {/* Actions for this domain */}
                      <div className="space-y-1 pl-2">
                        {domainActions.map((action) => {
                          const currentIndex = actionCounter++;
                          return (
                            <ActionRow
                              key={action.id}
                              action={action}
                              onToggleComplete={onToggleAction}
                              onUpdateTitle={onUpdateAction}
                              onPlanInAgenda={onPlanAction}
                              onDelete={onDeleteAction}
                              isFocusAction={action.id === focusActionId}
                              actionNumber={startActionNumber !== undefined ? currentIndex : undefined}
                              showPriorityBadge={true}
                              showTypeBadge={true}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ));
                })()}
                {/* Add action input */}
                {isAddingAction ? (
                  <div className="flex items-center space-x-2 py-1">
                    <input
                      type="text"
                      value={newActionTitle}
                      onChange={(e) => setNewActionTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAddAction();
                        if (e.key === 'Escape') {
                          setIsAddingAction(false);
                          setNewActionTitle('');
                        }
                      }}
                      onBlur={() => {
                        if (!newActionTitle.trim()) {
                          setIsAddingAction(false);
                        }
                      }}
                      placeholder="Nieuwe actie..."
                      className="
                        flex-1 px-2 py-1 text-xs
                        border border-gray-300 rounded-md
                        focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500
                      "
                      autoFocus
                    />
                    <button
                      onClick={handleAddAction}
                      className="text-xs text-orange-600 hover:text-orange-700 px-2"
                    >
                      Toevoegen
                    </button>
                  </div>
                ) : (
                  onAddAction && (
                    <button
                      onClick={() => setIsAddingAction(true)}
                      className="
                        text-xs text-gray-400 hover:text-gray-600
                        py-1 flex items-center space-x-1
                        transition-colors duration-150
                      "
                    >
                      <span>+</span>
                      <span>Nieuwe actie</span>
                    </button>
                  )
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MilestoneRow;
